import React, { useEffect, useMemo, useState } from 'react';
import { Form, FormGroup, Label, Input, Modal, ModalHeader, ModalBody, ModalFooter, Button, Nav, NavItem, NavLink } from 'reactstrap';
import Layout from '../components/Layout';
import api from '../services/api';

const LeaguePayments = () => {
  const [leagues, setLeagues] = useState([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState(null);
  const [paymentView, setPaymentView] = useState('team');
  const [leagueTeams, setLeagueTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [leaguesLoading, setLeaguesLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [playerToUncheck, setPlayerToUncheck] = useState(null);
  const [paymentStatusField, setPaymentStatusField] = useState('paid');

  useEffect(() => {
    const fetchLeagues = async () => {
      try {
        const response = await api.get('/leagues/');
        const allLeagues = Array.isArray(response.data) ? response.data : [];
        const leagueList = allLeagues.filter((league) => {
          return league?.state === 'Active' || league?.state === 'Registration';
        });
        setLeagues(leagueList);
        
        // Auto-select first league if available
        if (leagueList.length > 0) {
          setSelectedLeagueId(leagueList[0].id);
        }
      } catch (error) {
        console.error('Error loading leagues:', error);
      } finally {
        setLeaguesLoading(false);
      }
    };

    fetchLeagues();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedLeagueId) return;
      
      setLoading(true);
      try {
        const teamsResponse = await api.get(`/league-teams/?league=${selectedLeagueId}`);
        setLeagueTeams(teamsResponse.data || []);
      } catch (error) {
        console.error('Error loading league players:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedLeagueId]);

  const players = useMemo(() => {
    const flattened = leagueTeams.flatMap((lt) =>
      (lt.players || []).map((player) => ({
        ...player,
        team_display: lt.team_display || 'Team',
      }))
    );

    return flattened.sort((a, b) => {
      const lastA = (a.last_name || '').toLowerCase();
      const lastB = (b.last_name || '').toLowerCase();
      if (lastA !== lastB) return lastA.localeCompare(lastB);

      const firstA = (a.first_name || '').toLowerCase();
      const firstB = (b.first_name || '').toLowerCase();
      if (firstA !== firstB) return firstA.localeCompare(firstB);

      const teamA = (a.team_display || '').toLowerCase();
      const teamB = (b.team_display || '').toLowerCase();
      return teamA.localeCompare(teamB);
    });
  }, [leagueTeams]);

  const fetchPlayers = async () => {
    if (!selectedLeagueId) return;
    try {
      const teamsResponse = await api.get(`/league-teams/?league=${selectedLeagueId}`);
      setLeagueTeams(teamsResponse.data || []);
    } catch (error) {
      console.error('Error loading league players:', error);
    }
  };

  const handlePaymentStatusChange = async (player, paid, field) => {
    // Confirm only when changing a paid player to unpaid.
    if (player[field] && !paid) {
      setPlayerToUncheck(player);
      setPaymentStatusField(field);
      setModalOpen(true);
      return;
    }

    try {
      await api.put(`/team-players/${player.id}/`, {
        ...player,
        [field]: paid,
      });
      fetchPlayers();
    } catch (error) {
      console.error(`Error updating player ${field} status:`, error);
    }
  };

  const confirmUncheck = async () => {
    if (!playerToUncheck) return;

    try {
      await api.put(`/team-players/${playerToUncheck.id}/`, {
        ...playerToUncheck,
        [paymentStatusField]: false,
      });
      fetchPlayers();
      setModalOpen(false);
      setPlayerToUncheck(null);
    } catch (error) {
      console.error(`Error updating player ${paymentStatusField} status:`, error);
      setModalOpen(false);
      setPlayerToUncheck(null);
    }
  };

  const cancelUncheck = () => {
    setModalOpen(false);
    setPlayerToUncheck(null);
  };

  return (
    <Layout>
      <div className="container my-4">
        <style>{`
          @media print {
            .print-button { display: none !important; }
            .print-container { padding: 0 !important; }
            body > * { margin: 0; padding: 0; }
            /* Hide Layout components except the main content */
            .col-sm-4, .nav, [class*="sidebar"], header, nav { display: none !important; }
            .container { width: 100% !important; max-width: 100% !important; margin: 0 !important; padding: 10px !important; }
          }
          .page-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1.5rem;
          }
          .player-row {
            display: grid;
            grid-template-columns: 110px 110px 1fr 1fr 1fr;
            gap: 12px;
            align-items: center;
            padding: 6px 0;
            border-bottom: 1px solid #e6e6e6;
          }
          .player-row:last-child { border-bottom: none; }
          .payment-status-select {
            min-width: 100px;
          }
          .payment-status-select.paid {
            background-color: #d1e7dd;
          }
          .payment-status-select.unpaid {
            background-color: #f8d7da;
          }
          .team-payment-group {
            border: 1px solid #e6e6e6;
            margin-bottom: 1rem;
          }
          .team-payment-heading {
            background: #f8f9fa;
            font-weight: 600;
            padding: 0.65rem 0.75rem;
          }
        `}</style>

        <div className="print-container">
          <div className="page-header">
            <h2>League Payments</h2>
            <button type="button" className="btn btn-primary btn-sm print-button" onClick={() => window.print()}>
              Print
            </button>
          </div>

          {leaguesLoading && <p className="text-muted">Loading leagues...</p>}

          {!leaguesLoading && leagues.length === 0 && (
            <p className="text-muted">No active or registration leagues found.</p>
          )}

          {!leaguesLoading && leagues.length > 0 && (
            <>
              <Form className="mb-4">
                <FormGroup>
                  <Label for="league-select">Select League</Label>
                  <Input
                    id="league-select"
                    type="select"
                    value={selectedLeagueId || ''}
                    onChange={(e) => setSelectedLeagueId(Number(e.target.value))}
                  >
                    {leagues.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.title}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Form>

              <Nav tabs className="mb-3">
                <NavItem>
                  <NavLink
                    className={paymentView === 'team' ? 'active' : ''}
                    onClick={() => setPaymentView('team')}
                    style={{ cursor: 'pointer' }}
                  >
                    By Team
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={paymentView === 'person' ? 'active' : ''}
                    onClick={() => setPaymentView('person')}
                    style={{ cursor: 'pointer' }}
                  >
                    By Person
                  </NavLink>
                </NavItem>
              </Nav>

              {loading && <p className="text-muted">Loading players...</p>}

              {!loading && selectedLeagueId && players.length === 0 && (
                <p className="text-muted">No players found for this league.</p>
              )}

              {!loading && selectedLeagueId && players.length > 0 && paymentView === 'person' && (
                <div>
                  <div className="player-row fw-semibold">
                    <div>League</div>
                    <div>Prize</div>
                    <div>Name</div>
                    <div>Team</div>
                    <div>Email</div>
                  </div>
                  {players.map((player) => (
                    <div className="player-row" key={player.id}>
                      <Input
                        type="select"
                        className={`payment-status-select ${player.paid ? 'paid' : 'unpaid'}`}
                        value={player.paid ? 'paid' : 'not-paid'}
                        onChange={(event) => handlePaymentStatusChange(player, event.target.value === 'paid', 'paid')}
                      >
                        <option value="paid">Paid</option>
                        <option value="not-paid">Unpaid</option>
                      </Input>
                      <Input
                        type="select"
                        className={`payment-status-select ${player.paid_prize ? 'paid' : 'unpaid'}`}
                        value={player.paid_prize ? 'paid' : 'not-paid'}
                        onChange={(event) => handlePaymentStatusChange(player, event.target.value === 'paid', 'paid_prize')}
                      >
                        <option value="paid">Paid</option>
                        <option value="not-paid">Unpaid</option>
                      </Input>
                      <div>{player.last_name}, {player.first_name}</div>
                      <div>{player.team_display}</div>
                      <div>{player.email || '-'}</div>
                    </div>
                  ))}
                </div>
              )}

              {!loading && selectedLeagueId && players.length > 0 && paymentView === 'team' && (
                <div>
                  {leagueTeams.map((leagueTeam) => (
                    <div className="team-payment-group" key={leagueTeam.id}>
                      <div className="team-payment-heading">{leagueTeam.team_display || 'Team'}</div>
                      <div className="px-3">
                        <div className="player-row fw-semibold">
                          <div>League</div>
                          <div>Prize</div>
                          <div>Name</div>
                          <div>Email</div>
                          <div></div>
                        </div>
                        {(leagueTeam.players || []).map((player) => (
                          <div className="player-row" key={player.id}>
                            <Input
                              type="select"
                              className={`payment-status-select ${player.paid ? 'paid' : 'unpaid'}`}
                              value={player.paid ? 'paid' : 'not-paid'}
                              onChange={(event) => handlePaymentStatusChange(player, event.target.value === 'paid', 'paid')}
                            >
                              <option value="paid">Paid</option>
                              <option value="not-paid">Unpaid</option>
                            </Input>
                            <Input
                              type="select"
                              className={`payment-status-select ${player.paid_prize ? 'paid' : 'unpaid'}`}
                              value={player.paid_prize ? 'paid' : 'not-paid'}
                              onChange={(event) => handlePaymentStatusChange(player, event.target.value === 'paid', 'paid_prize')}
                            >
                              <option value="paid">Paid</option>
                              <option value="not-paid">Unpaid</option>
                            </Input>
                            <div>{player.last_name}, {player.first_name}</div>
                            <div>{player.email || '-'}</div>
                            <div></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Modal isOpen={modalOpen} toggle={cancelUncheck}>
        <ModalHeader toggle={cancelUncheck}>Confirm</ModalHeader>
        <ModalBody>
          {playerToUncheck && (
            <p>
              Are you sure you want to mark <strong>{playerToUncheck.first_name} {playerToUncheck.last_name}</strong> as unpaid{paymentStatusField === 'paid_prize' ? ' for their prize' : ''}?
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={cancelUncheck}>
            Cancel
          </Button>
          <Button color="danger" onClick={confirmUncheck}>
            Mark as Unpaid
          </Button>
        </ModalFooter>
      </Modal>
    </Layout>
  );
};

export default LeaguePayments;
